import { useCallback } from 'react';
import { Platform } from 'react-native';

import { useIsLoggedIn } from '@order/AuthMachine';
import { APPLE_PAY } from '@order/constants';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  usePaymentProfileQuery,
  useSetDefaultPaymentMethodMutation,
} from '../graphql/PaymentProfile.generated';

/**
 * Handling querying for `isApplePayDefault`,
 * And updating it with `updateDefaultPaymentMethodToApplePay`.
 * This is a direct link to the customer's payment profile "default_card_id".
 */

export function useApplePayPaymentMethod() {
  const isLoggedIn = useIsLoggedIn();
  const isApplePayEnabled = useFeatureFlag('permanent-apple-pay-enabled');

  // ─── Graphql ─────────────────────────────────────────────────────────

  const [{ data, fetching: isFetching }] = usePaymentProfileQuery({
    pause: !isLoggedIn || !isApplePayEnabled,
    requestPolicy: 'network-only',
  });

  const [{ fetching: isUpdating }, setDefaultPaymentMethod] =
    useSetDefaultPaymentMethodMutation();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const updateDefaultPaymentMethodToApplePay = useCallback(async () => {
    return setDefaultPaymentMethod({ paymentMethodId: APPLE_PAY });
  }, [setDefaultPaymentMethod]);

  const defaultId = data?.paymentProfile?.defaultCardId;

  // ─── Flags ───────────────────────────────────────────────────────────

  const isApplePaySupported = Boolean(isApplePayEnabled && checkCanApplePay());
  const isApplePayDefault = isApplePaySupported && defaultId === APPLE_PAY;

  return {
    isLoadingDefaultPaymentMethodId: isFetching,
    isUpdatingDefaultPaymentMethodId: isUpdating,
    isApplePaySupported,
    isApplePayDefault,
    updateDefaultPaymentMethodToApplePay,
  };
}

function checkCanApplePay() {
  return Platform.select({
    ios: true,
    android: false,
    web: 'ApplePaySession' in window,
  });
}
